import { createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router';
import { ArrowLeft, MessageCircle, PackageSearch, Truck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '@nokowazzi/api-client';
import type { Money } from '@nokowazzi/types';
import { formatMoney } from '@nokowazzi/types';
import {
  Avatar,
  Badge,
  Button,
  Dialog,
  Divider,
  EmptyState,
  EscrowStatusBar,
  IconButton,
  PriceTag,
  ReviewCard,
  Skeleton,
  toast,
  TopAppBar,
  TrustBadge,
} from '@nokowazzi/ui';
import { requireAuthBeforeLoad } from '../features/auth/guard';
import {
  orderRoleOf,
  useConfirmOrder,
  useOrderQuery,
  usePayOrder,
  useCancelOrder,
  type OrderAction,
  type OrderCounterpartDto,
  type OrderDetailDto,
} from '../features/orders/api';
import { DisputeSheet } from '../features/orders/DisputeSheet';
import { escrowStatusLabels, statusBadgeTone } from '../features/orders/format';
import { ReviewSheet } from '../features/orders/ReviewSheet';
import { ShipmentSheet } from '../features/orders/ShipmentSheet';
import { avatarUrl } from '../features/sourcing/urls';
import { useViewerCurrency } from '../features/sourcing/viewer';
import { convertForViewer, useFxRates } from '../lib/fx';
import { useAuthStore } from '../stores/auth';

export const Route = createFileRoute('/orders/$orderId')({
  beforeLoad: ({ params }) => requireAuthBeforeLoad(`/orders/${params.orderId}`),
  component: OrderDetailScreen,
});

function DetailSkeleton() {
  return (
    <div className="flex flex-col gap-4 px-5 pt-2" aria-busy="true">
      <Skeleton className="h-16 w-full" />
      <Skeleton className="h-32 w-full rounded-lg" />
      <Skeleton className="h-20 w-full rounded-lg" />
    </div>
  );
}

function OrderDetailScreen() {
  const { t } = useTranslation();
  const { orderId } = Route.useParams();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const navigate = Route.useNavigate();

  const orderQuery = useOrderQuery(orderId);
  const detail = orderQuery.data;

  const handleBack = () => {
    if (canGoBack) {
      router.history.back();
    } else {
      void navigate({ to: '/orders' });
    }
  };

  const isNotFound = orderQuery.error instanceof ApiError && orderQuery.error.status === 404;

  return (
    <div className="flex min-h-dvh flex-col bg-page">
      <TopAppBar
        title={t('escrow.detail.title')}
        leading={
          <IconButton aria-label={t('actions.back')} onClick={handleBack}>
            <ArrowLeft aria-hidden="true" />
          </IconButton>
        }
      />

      {orderQuery.isPending ? (
        <DetailSkeleton />
      ) : orderQuery.isError || detail === undefined ? (
        <EmptyState
          className="mt-16"
          icon={<PackageSearch size={48} strokeWidth={1.5} />}
          title={isNotFound ? t('errors.notFound') : t('errors.generic')}
          action={
            isNotFound ? undefined : (
              <Button variant="tertiary" onClick={() => void orderQuery.refetch()}>
                {t('actions.retry')}
              </Button>
            )
          }
        />
      ) : (
        <DetailBody detail={detail} orderId={orderId} />
      )}
    </div>
  );
}

interface CounterpartCardProps {
  counterpart: OrderCounterpartDto;
}

function CounterpartCard({ counterpart }: CounterpartCardProps) {
  const { t } = useTranslation();
  return (
    <div className="flex items-center gap-3 rounded-lg border border-line bg-surface p-4">
      <Avatar
        name={counterpart.display_name}
        src={avatarUrl(counterpart.avatar_path)}
        size="md"
      />
      <div className="flex min-w-0 flex-col">
        <span className="truncate text-body font-semibold text-fg">
          {counterpart.display_name}
        </span>
        <TrustBadge
          altitude={counterpart.trust_score}
          size="sm"
          meterAriaLabel={t('trust.meterLabel')}
        />
      </div>
    </div>
  );
}

interface AmountRowProps {
  label: string;
  money: Money;
  emphasis?: boolean;
}

function AmountRow({ label, money, emphasis = false }: AmountRowProps) {
  const fxRates = useFxRates();
  const viewerCurrency = useViewerCurrency();
  // 주 표기는 주문 통화, 보조는 보는 사람 통화 환산(동일 통화면 미표기)
  const converted = convertForViewer(money, viewerCurrency, fxRates.data);
  return (
    <div className="flex items-baseline justify-between gap-3">
      <span className={emphasis ? 'text-body font-semibold text-fg' : 'text-body text-fg-secondary'}>
        {label}
      </span>
      <PriceTag
        size={emphasis ? 'md' : 'sm'}
        emphasis={emphasis}
        money={money}
        converted={converted ? { money: converted } : undefined}
      />
    </div>
  );
}

interface DetailBodyProps {
  detail: OrderDetailDto;
  orderId: string;
}

function DetailBody({ detail, orderId }: DetailBodyProps) {
  const { t } = useTranslation();
  const navigate = Route.useNavigate();
  const myId = useAuthStore((state) => state.session?.user.id);

  const { order, request, buyer, seller, shipment, review } = detail;
  const role = orderRoleOf(order, myId);
  const counterpart = role === 'buyer' ? seller : buyer;

  // 서버가 내려준 역할별 허용 액션 (도메인 allowedActions 결과)
  const myActions: OrderAction[] =
    role === 'buyer'
      ? detail.allowedActions.buyer
      : role === 'seller'
        ? detail.allowedActions.seller
        : [];
  const can = (action: OrderAction) => myActions.includes(action);

  const currency = order.currency;
  const itemMoney: Money = { amount: order.item_amount, currency };
  const shippingMoney: Money = { amount: order.shipping_amount, currency };
  const feeMoney: Money = { amount: order.fee_amount, currency };
  const totalMoney: Money = { amount: order.total_amount, currency };
  const payoutMoney: Money | null =
    order.seller_payout === null ? null : { amount: order.seller_payout, currency };

  const payOrder = usePayOrder(orderId);
  const confirmOrder = useConfirmOrder(orderId);
  const cancelOrder = useCancelOrder(orderId);

  const [payOpen, setPayOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [shipmentOpen, setShipmentOpen] = useState(false);
  const [disputeOpen, setDisputeOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);

  const openChat = () => {
    if (detail.roomId !== null) {
      void navigate({ to: '/chat/$roomId', params: { roomId: detail.roomId } });
    } else {
      void navigate({ to: '/sourcing/$requestId', params: { requestId: request.id } });
    }
  };

  const handlePay = () => {
    payOrder.mutate(undefined, {
      onSuccess: () => {
        setPayOpen(false);
        toast.show({ type: 'success', message: t('escrow.pay.success') });
      },
      onError: () => toast.show({ type: 'error', message: t('escrow.pay.error') }),
    });
  };

  const handleConfirm = () => {
    confirmOrder.mutate(undefined, {
      onSuccess: () => {
        setConfirmOpen(false);
        toast.show({ type: 'success', message: t('escrow.confirm.success') });
        // 정산 완료 후 리뷰 유도
        setReviewOpen(true);
      },
      onError: () => toast.show({ type: 'error', message: t('errors.generic') }),
    });
  };

  const handleCancel = () => {
    cancelOrder.mutate(
      {},
      {
        onSuccess: () => {
          setCancelOpen(false);
          toast.show({ type: 'success', message: t('escrow.cancel.success') });
        },
        onError: () => toast.show({ type: 'error', message: t('errors.generic') }),
      },
    );
  };

  // 리뷰 완료/분쟁 상태 안내
  const alreadyReviewed = review !== null;
  const canReview = order.status === 'settled' && !alreadyReviewed && role !== null;

  // 하단 고정 CTA 후보 (allowedActions + 역할 기반)
  const showPay = role === 'buyer' && can('pay');
  const showShip = role === 'seller' && can('ship');
  const showConfirm = role === 'buyer' && can('confirm');
  const showCancel = can('cancel');
  const showDispute = can('open_dispute');

  const hasBottomBar =
    showPay || showShip || showConfirm || showCancel || showDispute || canReview;

  return (
    <>
      <main
        className={
          hasBottomBar
            ? 'flex flex-1 flex-col gap-5 px-5 pt-2 pb-[calc(var(--safe-bottom)+104px)]'
            : 'flex flex-1 flex-col gap-5 px-5 pt-2 pb-[calc(var(--safe-bottom)+24px)]'
        }
      >
        {/* 진행 상태 */}
        <section className="flex flex-col gap-3 pt-1">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-caption1 text-fg-secondary">{request.title}</h2>
            <Badge tone={statusBadgeTone(order.status)}>
              {t(`escrow.status.${order.status}`)}
            </Badge>
          </div>
          <EscrowStatusBar status={order.status} labels={escrowStatusLabels(t)} />
        </section>

        <Divider />

        {/* 주문 요약 */}
        <section className="flex flex-col gap-3">
          <div className="flex flex-col gap-1">
            <span className="text-caption1 text-fg-secondary">{t('escrow.detail.product')}</span>
            <span className="text-body font-medium text-fg">{request.title}</span>
          </div>
          <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-4">
            <AmountRow label={t('escrow.detail.itemAmount')} money={itemMoney} />
            {order.shipping_amount > 0 ? (
              <AmountRow label={t('escrow.detail.shippingAmount')} money={shippingMoney} />
            ) : null}
            <AmountRow label={t('escrow.detail.feeAmount')} money={feeMoney} />
            <Divider className="my-1" />
            {/* 판매자에게는 정산액(= 서버 계산 payout), 구매자에게는 총 결제액 */}
            {role === 'seller' && payoutMoney !== null ? (
              <AmountRow label={t('escrow.detail.payoutAmount')} money={payoutMoney} emphasis />
            ) : (
              <AmountRow label={t('escrow.detail.totalAmount')} money={totalMoney} emphasis />
            )}
          </div>
        </section>

        {/* 상대 */}
        <section className="flex flex-col gap-2">
          <span className="text-caption1 text-fg-secondary">{t('escrow.detail.counterpart')}</span>
          <CounterpartCard counterpart={counterpart} />
        </section>

        {/* 배송 정보 */}
        {shipment !== null ? (
          <section className="flex flex-col gap-2">
            <span className="text-caption1 text-fg-secondary">{t('escrow.detail.shipment')}</span>
            <div className="flex flex-col gap-2 rounded-lg border border-line bg-surface p-4">
              <div className="flex items-center gap-2 text-body text-fg">
                <Truck className="size-5 shrink-0 text-fg-secondary" aria-hidden="true" />
                <span className="font-medium">{shipment.carrier}</span>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-caption1 text-fg-secondary">
                  {t('escrow.detail.trackingNumber')}
                </span>
                <span className="text-body tabular-nums text-fg">{shipment.tracking_number}</span>
              </div>
            </div>
          </section>
        ) : null}

        {/* 채팅 바로가기 */}
        <Button variant="tertiary" fullWidth leftIcon={<MessageCircle className="size-5" aria-hidden="true" />} onClick={openChat}>
          {t('escrow.actions.openChat')}
        </Button>

        {/* 리뷰 / 분쟁 안내 */}
        {alreadyReviewed && review !== null ? (
          <section className="flex flex-col gap-2">
            <span className="text-caption1 text-fg-secondary">{t('escrow.detail.reviewDone')}</span>
            <ReviewCard
              review={{
                id: review.id,
                reviewerName: counterpart.display_name,
                reviewerAvatarUrl: avatarUrl(counterpart.avatar_path),
                rating: review.rating,
                tags: review.tags,
                comment: review.comment ?? undefined,
                dateLabel: new Date(review.created_at).toLocaleDateString(),
              }}
            />
          </section>
        ) : null}

        {order.status === 'disputed' ? (
          <p className="rounded-lg bg-warning-50 px-3 py-2 text-caption1 text-warning-700">
            {t('escrow.detail.disputeOngoing')}
          </p>
        ) : null}
      </main>

      {/* 하단 고정 CTA */}
      {hasBottomBar ? (
        <div className="fixed inset-x-0 bottom-0 z-40 flex flex-col gap-2 border-t border-line bg-page px-5 pt-3 pb-[calc(var(--safe-bottom)+12px)]">
          {showPay ? (
            <Button variant="primary" size="lg" fullWidth onClick={() => setPayOpen(true)}>
              {t('escrow.actions.pay')}
            </Button>
          ) : null}
          {showShip ? (
            <Button variant="primary" size="lg" fullWidth onClick={() => setShipmentOpen(true)}>
              {t('escrow.actions.ship')}
            </Button>
          ) : null}
          {showConfirm ? (
            <Button variant="primary" size="lg" fullWidth onClick={() => setConfirmOpen(true)}>
              {t('escrow.actions.confirm')}
            </Button>
          ) : null}
          {canReview ? (
            <Button variant="primary" size="lg" fullWidth onClick={() => setReviewOpen(true)}>
              {t('escrow.actions.writeReview')}
            </Button>
          ) : null}
          {showCancel ? (
            <Button variant="ghost" fullWidth onClick={() => setCancelOpen(true)}>
              {t('escrow.actions.cancel')}
            </Button>
          ) : null}
          {showDispute ? (
            <Button
              variant="ghost"
              fullWidth
              className="text-danger-600 hover:bg-danger-50"
              onClick={() => setDisputeOpen(true)}
            >
              {t('escrow.actions.openDispute')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {/* 결제 확인 Dialog */}
      <Dialog
        open={payOpen}
        onOpenChange={(next) => {
          if (payOrder.isPending) return;
          setPayOpen(next);
        }}
        title={t('escrow.pay.title')}
        description={t('escrow.pay.description')}
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setPayOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button variant="primary" fullWidth loading={payOrder.isPending} onClick={handlePay}>
              {t('escrow.pay.confirm', { amount: formatMoney(totalMoney) })}
            </Button>
          </>
        }
      />

      {/* 수령 확인 Dialog */}
      <Dialog
        open={confirmOpen}
        onOpenChange={(next) => {
          if (confirmOrder.isPending) return;
          setConfirmOpen(next);
        }}
        title={t('escrow.confirm.title')}
        description={t('escrow.confirm.description')}
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setConfirmOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button
              variant="primary"
              fullWidth
              loading={confirmOrder.isPending}
              onClick={handleConfirm}
            >
              {t('escrow.confirm.submit')}
            </Button>
          </>
        }
      />

      {/* 취소 확인 Dialog */}
      <Dialog
        open={cancelOpen}
        onOpenChange={(next) => {
          if (cancelOrder.isPending) return;
          setCancelOpen(next);
        }}
        title={t('escrow.cancel.title')}
        description={t('escrow.cancel.description')}
        footer={
          <>
            <Button variant="tertiary" fullWidth onClick={() => setCancelOpen(false)}>
              {t('actions.close')}
            </Button>
            <Button
              variant="danger"
              fullWidth
              loading={cancelOrder.isPending}
              onClick={handleCancel}
            >
              {t('escrow.cancel.submit')}
            </Button>
          </>
        }
      />

      <ShipmentSheet open={shipmentOpen} onOpenChange={setShipmentOpen} orderId={orderId} />
      <DisputeSheet open={disputeOpen} onOpenChange={setDisputeOpen} orderId={orderId} />
      <ReviewSheet open={reviewOpen} onOpenChange={setReviewOpen} orderId={orderId} />
    </>
  );
}
